import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import Flags from "common/constants/flags";
import type { UserNotificationType } from "common/types/userNotification";
import { assertIsNotNull } from "common/utils/assertOfType";
import { bypassCognito } from "common/utils/context";
import { isHigharcStaffBootstrapEnv } from "common/utils/env";
import { isHigharcDemoAccountEmail } from "common/utils/higharcDemoAccountEmail";
import { isHigharcEmailAddress } from "common/utils/higharcEmail";
import { mutate } from "swr";
import * as cognito from "./cognito";
import * as cookie from "./cookie";
import { accountSwitcherPath, adminPath, builderHomePagePath, loginPath, ssoCallbackPath } from "./routeHelpers";
import { userIsSuperAdmin } from "./security/shared";
import { putUser, syncUser, syncInternalUserSuperAdminRole } from "./storage/userStorage";
import { decodeBase64Utf8, encodeBase64Utf8, encodeRandomBase64Url } from "./utils/base64";
import { getSafeRedirectPath } from "./utils/safeRedirectPath";

export const VALIDATION_ENDPOINT = `${WEB_API_BASE_PATH}/users/me`;

type AuthResponse = Promise<[string, null] | [null, UserNotificationType]>;

export const login = async (
  email: string,
  password: string,
  redirectTo?: string,
  onError?: (e: any) => void,
): Promise<[string, null] | [null, UserNotificationType | null]> => {
  if (bypassCognito()) {
    return await postLoginSuccessWithoutCognito(email, redirectTo);
  }

  try {
    await cognito.logIn(email, password);
    return await postLoginSuccess(redirectTo);
  } catch (e) {
    // User not found in the new pool — attempt USER_PASSWORD_AUTH which triggers
    // the UserMigration Lambda to transparently migrate from the legacy pool.
    if (e?.code === "UserNotFoundException" || e?.name === "UserNotFoundException") {
      return await loginWithPasswordAuth(email, password, redirectTo, onError);
    }

    if (onError) {
      onError(e);
      return [null, null];
    }
    console.error(e);
    return [null, { messageType: "error", message: String(e.message) }];
  }
};

const loginWithPasswordAuth = async (
  email: string,
  password: string,
  redirectTo?: string,
  onError?: (e: any) => void,
): Promise<[string, null] | [null, UserNotificationType | null]> => {
  try {
    const {
      idToken,
      refreshToken,
      email: resolvedEmail,
      firstName,
      lastName,
    } = await cognito.initiatePasswordAuth(email, password);
    if (refreshToken) {
      cognito.setSsoRefreshToken(refreshToken);
    }
    return await postLoginSuccessFromToken(resolvedEmail, idToken, firstName, lastName, redirectTo);
  } catch (e) {
    if (onError) {
      onError(e);
      return [null, null];
    }
    console.error(e);
    return [null, { messageType: "error", message: String(e.message) }];
  }
};

export const logout = async (userId: string, noRedirect?: boolean): Promise<void> => {
  try {
    // clear any role impersonation settings
    await putUser(userId, { tempUserRole: null });

    if (!bypassCognito()) {
      cognito.logOut();
    }

    cookie.remove(cognito.HIGHARC_USER_COOKIE);
    cognito.clearSsoRefreshToken();

    // Only reason to make this true is for user invite screen
    if (!noRedirect) {
      window.location.href = "/account/login";
    }
  } catch (e) {
    console.error(e);
  }
};

export const getPostLoginRedirect = async (user: ClientUserHydrated | null, redirectTo?: string): AuthResponse => {
  const safeRedirectTo = getSafeRedirectPath(redirectTo);

  if (!user) {
    return [loginPath(safeRedirectTo), null];
  }

  // Internal Higharc user bootstrap for empty sandbox/PR/local DBs only (DB_ENV gate —
  // those envs share develop's Cognito pool, so Cognito env alone is insufficient).
  // Skip when they already belong to a builder — e.g. invited as Drafter with adminRole NONE.
  // Intentionally skip for demo accounts (these accounts are used by external users)
  if (
    user.email &&
    isHigharcStaffBootstrapEnv() &&
    isHigharcEmailAddress(user.email) &&
    user.adminRole === "NONE" &&
    user.builderAccounts.length === 0 &&
    !isHigharcDemoAccountEmail(user.email)
  ) {
    await syncInternalUserSuperAdminRole(user.email);

    // update SWR
    await mutate(VALIDATION_ENDPOINT, { ...user, adminRole: "SUPER_ADMIN" });

    if (safeRedirectTo) {
      return [safeRedirectTo, null];
    }
    return [adminPath(), null];
  }

  if (safeRedirectTo) {
    return [safeRedirectTo, null];
  }

  if (userIsSuperAdmin(user)) {
    return [adminPath(), null];
  }

  if (user.builderAccounts.length === 1) {
    return [builderHomePagePath(user.builderAccounts[0].builderId), null];
  } else if (user.builderAccounts.length > 1) {
    return [accountSwitcherPath(), null];
  }

  // Hitting this means something is wrong, likely due to a user that is:
  //   1. Not a member of any builder
  //   2. Not a Super or System Admin
  // Previously, returning "/" would cause infinite redirect loops in local dev.
  //
  // Now, we log the user out with a clear error message. This would likely only
  // ever be seen by an internal Higharc user.
  await logout(user.id, true);
  return [
    null,
    {
      messageType: "error",
      message: "User does not belong to any builder account, contact Higharc support for additional help.",
    },
  ];
};

const postLoginSuccess = async (redirectTo?: string): AuthResponse => {
  const cognitoUser = cognito.getUser();
  assertIsNotNull(cognitoUser);
  const session = await cognito.getSession(cognitoUser);
  const idToken = session.getIdToken();
  const { given_name: firstName, family_name: lastName } = idToken.payload;
  return postLoginSuccessFromToken(cognitoUser.getUsername(), idToken.getJwtToken(), firstName, lastName, redirectTo);
};

// Shared post-login logic for both the SRP and USER_PASSWORD_AUTH paths.
const postLoginSuccessFromToken = async (
  cognitoName: string,
  jwtToken: string,
  firstName: string,
  lastName: string,
  redirectTo?: string,
): AuthResponse => {
  cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ cognitoName, jwtToken }));

  const [user, msg] = await syncUser(cognitoName, firstName, lastName);
  if (msg) {
    return [null, msg];
  }

  await mutate(VALIDATION_ENDPOINT, user);
  return getPostLoginRedirect(user, redirectTo);
};

async function postLoginSuccessWithoutCognito(email: string, redirectTo?: string): AuthResponse {
  cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ cognitoName: email, jwtToken: email }));

  // Grab user by email
  const [user, msg] = await syncUser(email, "", "");
  if (msg) {
    return [null, msg];
  }

  // update SWR
  await mutate(VALIDATION_ENDPOINT, user);

  return getPostLoginRedirect(user, redirectTo);
}

/**
 * Initiates an SSO login by redirecting to the Cognito Hosted UI.
 * Generates a PKCE pair and stores the verifier in sessionStorage before
 * the redirect so it survives the round-trip through the identity provider.
 *
 * @param provider - The SSO provider to use. "Google" for Google Workspace; for SAML, pass the Cognito identity provider name (BuilderSSOConfig.idpIdentifier).
 * @param redirectTo - The URL to redirect to after successful login.
 * @returns A promise that resolves when the login is complete.
 */
export async function loginWithSSO(provider: string, redirectTo?: string): Promise<void> {
  const nonce = encodeRandomBase64Url(16);
  const safeRedirectTo = getSafeRedirectPath(redirectTo);
  // Cognito rejects URL-encoded JSON as a state value — base64 encode it per AWS docs:
  // https://docs.aws.amazon.com/cognito/latest/developerguide/authorization-endpoint.html
  const state = encodeBase64Utf8(JSON.stringify({ redirectTo: safeRedirectTo ?? "", nonce }));

  const { challenge } = await cognito.generatePKCEPair(state);

  const redirectUri = Flags.absoluteUrl(ssoCallbackPath());
  const domain = Flags.COGNITO_DOMAIN;
  const clientId = Flags.COGNITO_CLIENT_ID;

  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    identity_provider: provider,
    scope: "openid profile email",
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  });

  window.location.href = `${domain}/oauth2/authorize?${params.toString()}`;
}

/**
 * Handles the OAuth callback after the user returns from the Cognito Hosted UI.
 * Validates the state nonce, exchanges the code for tokens, sets the auth cookie,
 * syncs the user record, and navigates to the post-login destination.
 */
export async function handleOAuthCallback(
  code: string,
  state: string,
): Promise<[string, null] | [null, UserNotificationType]> {
  const storedState = cognito.getPKCEState();
  if (!storedState || storedState !== state) {
    return [null, { messageType: "error", message: "SSO login failed: invalid state parameter. Please try again." }];
  }

  let redirectTo: string | undefined;
  try {
    const parsed = JSON.parse(decodeBase64Utf8(state)) as { redirectTo?: string };
    redirectTo = getSafeRedirectPath(parsed.redirectTo || undefined);
  } catch {
    // Malformed state — proceed without a redirectTo
  }

  const redirectUri = Flags.absoluteUrl(ssoCallbackPath());
  const tokens = await cognito.exchangeCodeForTokens(code, redirectUri);

  cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ cognitoName: tokens.email, jwtToken: tokens.idToken }));

  const [user, msg] = await syncUser(tokens.email, tokens.firstName, tokens.lastName);
  if (msg) {
    return [null, msg];
  }

  await mutate(VALIDATION_ENDPOINT, user);

  return getPostLoginRedirect(user, redirectTo);
}
